import { logger } from '#~/logger/index.js';

const record = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : {};

// Allowlist configuration fields: URIs, credentials, tokens and response bodies stay out of traces.
export const describeAddDiscovery = (value: unknown) => {
  const discovery = record(value);
  const control = record(discovery.control);
  const video = record(discovery.video);
  return {
    discoveryId: discovery.id,
    agentId: discovery.agentId,
    irisEnabled: discovery.irisEnabled === true,
    brandId: discovery.brandId,
    modelId: discovery.modelId,
    brand: discovery.brand,
    modelName: discovery.modelName,
    hasVideo: discovery.video != null,
    hasControl: discovery.control != null,
    controlProtocol: control.protocol,
    controlHost: control.host,
    controlPort: control.port,
    videoProtocol: video.protocol,
    videoHost: video.host,
    videoPort: video.port,
    controlHasCredentials: Boolean(control.credentials),
    videoHasCredentials: Boolean(video.credentials)
  };
};

// Per rule: preserve-safe-diagnostic-error-codes — inspect nested causes without printing raw errors.
export const describeAddFailure = (error: unknown) => {
  const messages: string[] = [];
  const numericStatuses = new Set<number>();
  const seen = new Set<unknown>();
  const visit = (value: unknown, depth = 0): void => {
    if (depth > 8 || seen.has(value)) return;
    seen.add(value);
    if (typeof value === 'string') messages.push(value.slice(0, 16_384));
    else if (Array.isArray(value)) value.slice(0, 32).forEach((item) => visit(item, depth + 1));
    else if (value && typeof value === 'object') {
      const entry = record(value);
      for (const key of ['code', 'status']) {
        const code = entry[key];
        if (
          typeof code === 'number' &&
          [400, 401, 403, 404, 408, 409, 429, 500, 502, 503, 504].includes(code)
        )
          numericStatuses.add(code);
      }
      for (const key of ['name', 'message', 'cause', 'errors', 'code'])
        visit(entry[key], depth + 1);
    }
  };
  visit(error);
  const text = messages.join('\n');
  const codes = [
    ...new Set(
      (
        text.match(
          /\b(?:NQ-961|CD-712|TIMEOUT|TimeoutError|ETIMEDOUT|ECONNREFUSED|ECONNRESET|ENOTFOUND)\b/gi
        ) ?? []
      ).map((code) => code.toUpperCase())
    )
  ];
  const httpStatuses = [
    ...new Set([
      ...numericStatuses,
      ...[
        ...text.matchAll(
          /\b(?:status(?: code)?[: =]*|HTTP\s+)?(400|401|403|404|408|409|429|500|502|503|504)\s*[: ]\s*(?:Unauthorized|Forbidden|Bad Request|Not Found|Conflict|Too Many Requests|Internal Server Error|Bad Gateway|Service Unavailable|Gateway Timeout|Request Timeout)/gi
        )
      ].map((match) => Number(match[1]))
    ])
  ];
  const operations = [
    'GetCapabilities',
    'GetProfiles',
    'GetStreamURI',
    'while parsing response',
    'already exists',
    'failed to register device',
    'failed to build driver',
    'hostname is required',
    'port is required',
    'while parsing port'
  ].filter((operation) => text.toLowerCase().includes(operation.toLowerCase()));
  return {
    codes,
    httpStatuses,
    operations,
    classified: codes.length + httpStatuses.length + operations.length > 0
  };
};

export const describeOnvifObservation = (snapshot: unknown, id: string) => {
  const transient = record(record(snapshot)[id]);
  const control = record(transient.control);
  const capabilities = record(control.capabilities);
  const uri = record(capabilities.discovery).uri;
  return {
    snapshotPresent: snapshot != null,
    snapshotDeviceCount: Object.keys(record(snapshot)).length,
    transientPresent: record(snapshot)[id] != null,
    controlPresent: transient.control != null,
    capabilitiesPresent: control.capabilities != null,
    uriPresent: uri != null,
    uriType: typeof uri,
    uriProtocol: record(uri).protocol,
    controlAuthenticationStatus: transient.controlAuthenticationStatus
  };
};

export const describeBridgeDevice = (state: unknown, id: string) => {
  const control = record(record(state).control);
  const status = record(record(record(state).status).device)[id];
  const capabilities = record(record(status).capabilities);
  return {
    controlDevicePresent: record(control.device)[id] != null,
    studioDevicePresent: record(record(control.studio).device)[id] != null,
    statusDevicePresent: status != null,
    capabilitiesPresent: record(status).capabilities != null,
    streamUriPresent: Boolean(record(record(capabilities.video).discovered).uri)
  };
};

// Serialize now so DevTools cannot display a later, mutated version of the diagnostic object.
export const traceCameraAdd = (event: string, details: Record<string, unknown>): void => {
  try {
    logger.info(`[camera-add-trace] ${JSON.stringify({ event, ...details })}`);
  } catch {
    // Diagnostic output must not turn a successful add into a failed add.
  }
};

// Registry and candidate diagnostics expose identity and transport only, never URX or credentials.
export const describeDriverTemplate = (value: unknown) => {
  const template = record(value);
  const model = record(template.model);
  return {
    present: value != null,
    modelId: model.id,
    modelName: model.name,
    brandId: model.brandId,
    videoProtocol: record(template.video).protocol,
    controlProtocol: record(template.control).protocol
  };
};

const discoveryMappings = new Map<string, string>();
const hexId = (value: unknown): string | undefined =>
  value instanceof Uint8Array && value.length
    ? Array.from(value, (byte) => byte.toString(16).padStart(2, '0')).join('')
    : undefined;

// Deduplicate unchanged state snapshots; retain changes to identity even when the host stays the same.
export const traceDiscoveryMapping = (
  agentId: string,
  discoveryId: string,
  wire: unknown,
  mapped: unknown
): void => {
  const pb = record(wire);
  const summary = {
    agentId,
    discoveryId,
    wire: {
      source: pb.source,
      host: record(pb.network).host,
      brand: pb.brand,
      model: pb.model,
      brandId: hexId(pb.brandId),
      modelId: hexId(pb.modelId)
    },
    mapped: mapped == null ? null : describeAddDiscovery(mapped)
  };
  const key = `${agentId}:${discoveryId}`;
  const signature = JSON.stringify(summary);
  if (discoveryMappings.get(key) === signature) return;
  if (discoveryMappings.size >= 512)
    discoveryMappings.delete(discoveryMappings.keys().next().value!);
  discoveryMappings.set(key, signature);
  traceCameraAdd('fingerprint.discovery-mapped', summary);
};
